import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhTW from 'antd/locale/zh_TW';
import zhCN from 'antd/locale/zh_CN';
import { LocaleProvider, getStoredLocale } from './contexts/LocaleContext';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import MainLayout from './components/MainLayout';
import { Modules } from './constants/permissions';

const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const UserList = React.lazy(() => import('./pages/User/UserList'));
const UserForm = React.lazy(() => import('./pages/User/UserForm'));
const PermissionManagement = React.lazy(() => import('./pages/User/Permissions'));
const PersonalPermissionsManagement = React.lazy(() => import('./pages/User/PersonalPermissions'));
const PersonalPermissionEdit = React.lazy(() => import('./pages/User/PersonalPermissions/Edit'));
const UserMonitoring = React.lazy(() => import('./pages/User/UserMonitoring'));
const ChangePassword = React.lazy(() => import('./pages/User/ChangePassword'));
const DatabaseSearch = React.lazy(() => import('./pages/DatabaseSearch/index'));
const MekakPage = React.lazy(() => import('./pages/Mekak/MekakPage'));
const KnowledgeForm = React.lazy(() => import('./pages/Mekak/KnowledgeForm'));
const KnowledgeDetail = React.lazy(() => import('./pages/Mekak/KnowledgeDetail'));
const FileDocumentDetail = React.lazy(() => import('./pages/Mekak/FileDocumentDetail'));
const DevDashboard = React.lazy(() => import('./pages/DevDashboard'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const SCTracker = React.lazy(() => import('./pages/SCTracker'));
const FTracker = React.lazy(() => import('./pages/FTracker'));
const AuditDashboard = React.lazy(() => import('./pages/AuditDashboard'));
const ManagerDashboard = React.lazy(() => import('./pages/ManagerDashboard'));
const InboxPage = React.lazy(() => import('./pages/Inbox'));
const SystemAnnouncements = React.lazy(() => import('./pages/SystemAnnouncements'));
const WhiteboardList = React.lazy(() => import('./pages/Whiteboard'));
const WhiteboardEditor = React.lazy(() => import('./pages/Whiteboard/Editor'));
const ESP = React.lazy(() => import('./pages/ESP'));

function App() {
  const antdLocale = getStoredLocale() === 'zh_CN' ? zhCN : zhTW;

  return (
    <ErrorBoundary>
      <LocaleProvider>
      <ConfigProvider locale={antdLocale}>
        <Router>
          <AuthProvider>
            <Suspense fallback={<Spin size="large" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }} />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />

                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }>
                  {/* Default: Manager Dashboard */}
                  <Route index element={<ManagerDashboard />} />

                  {/* User Management — requires USER_MGMT permission */}
                  <Route path="user" element={<ProtectedRoute module={Modules.USER}><UserList /></ProtectedRoute>} />
                  <Route path="user/create" element={<ProtectedRoute module={Modules.USER}><UserForm mode="create" /></ProtectedRoute>} />
                  <Route path="user/edit/:userId" element={<ProtectedRoute module={Modules.USER}><UserForm mode="edit" /></ProtectedRoute>} />
                  <Route path="user/view/:userId" element={<ProtectedRoute module={Modules.USER}><UserForm mode="view" /></ProtectedRoute>} />
                  <Route path="user/permissions" element={<ProtectedRoute module={Modules.PERMISSION}><PermissionManagement /></ProtectedRoute>} />
                  <Route path="user/permissions/:userId" element={<ProtectedRoute module={Modules.PERMISSION}><UserForm mode="edit" /></ProtectedRoute>} />
                  <Route path="user/personal-permissions" element={<ProtectedRoute module={Modules.PERMISSION}><PersonalPermissionsManagement /></ProtectedRoute>} />
                  <Route path="user/personal-permissions/:userId" element={<ProtectedRoute module={Modules.PERMISSION}><PersonalPermissionEdit /></ProtectedRoute>} />
                  <Route path="user/monitoring" element={<ProtectedRoute module={Modules.USER}><UserMonitoring /></ProtectedRoute>} />
                  <Route path="user/change-password" element={<ChangePassword />} />

                  {/* Analytics */}
                  <Route path="analytics" element={<ProtectedRoute module={Modules.ANALYTICS}><Analytics /></ProtectedRoute>} />
                  {/* ERP架構分析已移入 /audit（資料庫監控）的 Tab — 舊網址導過去 */}
                  <Route path="erp-analysis" element={<Navigate to="/audit" replace />} />

                  {/* SC Tracker */}
                  <Route path="sc-tracker" element={<ProtectedRoute module={Modules.SC_TRACKER}><SCTracker /></ProtectedRoute>} />
                  <Route path="f-tracker" element={<ProtectedRoute module={Modules.F_TRACKER}><FTracker /></ProtectedRoute>} />

                  {/* Audit & Manager Dashboard */}
                  <Route path="audit" element={<ProtectedRoute module={Modules.AUDIT}><AuditDashboard /></ProtectedRoute>} />
                  <Route path="manager-dashboard" element={<ProtectedRoute module={Modules.MANAGER_DASHBOARD}><ManagerDashboard /></ProtectedRoute>} />

                  {/* Inbox */}
                  <Route path="inbox" element={<InboxPage />} />

                  {/* System Announcements */}
                  <Route path="system-announcements" element={<ProtectedRoute module={Modules.SYSTEM_ANNOUNCEMENTS}><SystemAnnouncements /></ProtectedRoute>} />

                  {/* Whiteboard (drawnix 腦圖+白板) */}
                  <Route path="whiteboard" element={<ProtectedRoute module={Modules.WHITEBOARD}><WhiteboardList /></ProtectedRoute>} />
                  <Route path="whiteboard/:id" element={<ProtectedRoute module={Modules.WHITEBOARD}><WhiteboardEditor /></ProtectedRoute>} />

                  {/* ESP平台 */}
                  <Route path="esp" element={<ProtectedRoute module={Modules.ESP}><ESP /></ProtectedRoute>} />

                  {/* Dev Dashboard */}
                  <Route path="dev-dashboard" element={<ProtectedRoute module={Modules.DEV_DASHBOARD}><DevDashboard /></ProtectedRoute>} />

                  {/* Database Search */}
                  <Route path="database-search" element={<ProtectedRoute module={Modules.DATABASE_SEARCH}><DatabaseSearch /></ProtectedRoute>} />

                  {/* MEKAK Knowledge Management */}
                  <Route path="mekak" element={<ProtectedRoute module={Modules.MEKAK}><MekakPage /></ProtectedRoute>} />
                  <Route path="mekak/knowledge/create" element={<ProtectedRoute module={Modules.MEKAK}><KnowledgeForm mode="create" /></ProtectedRoute>} />
                  <Route path="mekak/knowledge/:id" element={<ProtectedRoute module={Modules.MEKAK}><KnowledgeDetail /></ProtectedRoute>} />
                  <Route path="mekak/knowledge/:id/edit" element={<ProtectedRoute module={Modules.MEKAK}><KnowledgeForm mode="edit" /></ProtectedRoute>} />
                  <Route path="mekak/files/:id" element={<ProtectedRoute module={Modules.MEKAK}><FileDocumentDetail /></ProtectedRoute>} />

                  {/* Catch all - redirect to dashboard */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </Suspense>
          </AuthProvider>
        </Router>
      </ConfigProvider>
      </LocaleProvider>
    </ErrorBoundary>
  );
}

export default App;
